'use client';

import { Grid } from '@mantine/core';
import type { ReactNode } from 'react';

import type { PremiseResult } from '@dte/otw-azure-functions/src/common/interfaces/admin/search/PremiseResult';
import { getPremiseAddress } from '@dte/otw-azure-functions/src/common/premises/data/attributes/address/premiseAddress';
import { lowerCaseValue } from '@dte/otw-utils-core/src/strings/lowerCaseValue';
import { stableId } from '@dte/otw-utils-core/src/strings/stableId';
import { titleCaseValue } from '@dte/otw-utils-core/src/strings/titleCaseValue';

import { PremiseLink } from '../../../app/(client)/(authenticated)/premises/(premiseId)/premiseLink/PremiseLink';
import { PremiseMarker } from '../../../app/(client)/(authenticated)/premises/search/PremiseMarker';
import styles from './SearchBar.module.scss';

function longestTokenMatch(value: string, query: string | undefined): string | undefined {
	const lowerValue = lowerCaseValue(value);
	if (!lowerValue) {
		return undefined;
	}
	let longestMatch: string | undefined;
	for (const token of query?.split(' ') ?? []) {
		const lowerToken = lowerCaseValue(token);
		if (!lowerToken || !lowerValue.startsWith(lowerToken)) {
			continue;
		}
		if (!longestMatch || token.length > longestMatch.length) {
			longestMatch = token;
		}
	}
	return longestMatch;
}

export function HighlightedParts({
	value: inputValue,
	query,
	titleize,
}: {
	value: string | undefined;
	query: string | undefined;
	titleize?: boolean;
}): ReactNode {
	const value = titleize ? titleCaseValue(inputValue) : inputValue;
	if (!value) {
		return <span>&nbsp;</span>;
	}
	const valueParts = value.split(' ');
	const keyCount: Record<string, number> = {};
	return valueParts.map((part, index) => {
		const tokenLength = longestTokenMatch(part, query)?.length;
		const ending = index === valueParts.length - 1 ? null : ' ';
		const baseKey = stableId(part);
		const currentCount = keyCount[baseKey] ?? 1;
		keyCount[baseKey] = currentCount + 1;
		return (
			<span key={`${baseKey}-${currentCount}`}>
				{tokenLength ? (
					<>
						<b>{part.substring(0, tokenLength)}</b>
						{part.substring(tokenLength)}
					</>
				) : (
					part
				)}
				{ending}
			</span>
		);
	});
}

export function PremiseResultContent({
	premiseResult,
	queryInput = '',
	showLine2,
	linkAddress = false,
	showMarker = true,
	marker,
	trailing,
}: {
	premiseResult: PremiseResult;
	queryInput?: string;
	showLine2: boolean;
	linkAddress?: boolean;
	showMarker?: boolean;
	marker?: ReactNode;
	trailing?: ReactNode;
}): ReactNode {
	const address = getPremiseAddress(premiseResult.premiseData);
	if (!address) {
		return null;
	}
	const addressText = <HighlightedParts query={queryInput} value={address.line1} />;
	const addressLine = linkAddress ? (
		<PremiseLink premiseId={premiseResult.premiseId}>{addressText}</PremiseLink>
	) : (
		addressText
	);

	return (
		<Grid className={styles['optionRow']} gap="xs" align="flex-start">
			{(showMarker || marker !== undefined) && (
				<Grid.Col span="content" className={styles['marker']}>
					{marker ?? (
						<PremiseMarker premiseData={premiseResult.premiseData} size="small">
							&nbsp;
						</PremiseMarker>
					)}
				</Grid.Col>
			)}
			<Grid.Col span="auto" className={styles['optionContent']}>
				<Grid className={styles['premiseDetailsGrid']} gap="xs" align="center" columns={12}>
					<Grid.Col span="content" className={styles['addressLineDesktop']}>
						{addressLine}
					</Grid.Col>
					{showLine2 ? (
						<Grid.Col span="content" className={styles['addressLine2Desktop']}>
							<HighlightedParts query={queryInput} value={address.line2} titleize={true} />
						</Grid.Col>
					) : (
						<Grid.Col span="content" className={styles['addressLine2Placeholder']} aria-hidden="true">
							&nbsp;
						</Grid.Col>
					)}
					<Grid.Col span={12} className={styles['addressGroupMobile']}>
						<div className={styles['addressLine']}>{addressLine}</div>
						{showLine2 && (
							<div className={styles['addressLine2']}>
								<HighlightedParts query={queryInput} value={address.line2} titleize={true} />
							</div>
						)}
					</Grid.Col>
					<Grid.Col span="content" className={styles['cityDesktop']}>
						<HighlightedParts query={queryInput} value={address.city} />
					</Grid.Col>
					<Grid.Col span="content" className={styles['stateDesktop']}>
						<HighlightedParts query={queryInput} value={address.state} />
					</Grid.Col>
					<Grid.Col span={12} className={styles['cityStateMobile']}>
						<HighlightedParts query={queryInput} value={address.city} />,{' '}
						<HighlightedParts query={queryInput} value={address.state} />
					</Grid.Col>
				</Grid>
			</Grid.Col>
			{trailing !== undefined && (
				<Grid.Col span="content" style={{ textAlign: 'right' }}>
					{trailing}
				</Grid.Col>
			)}
		</Grid>
	);
}
